import { memo } from "react";
import { useTranslation } from "react-i18next";
import type { DeviceAccount } from "../modal/deviceAccounts";
import DeviceAccountList from "../modal/DeviceAccountList";
import { LockIcon, UserPlusIcon, WarningIcon } from "../modal/deviceModalIcons";
import {
  DeviceSectionCard,
  DeviceSectionHeader,
  DeviceSwitch,
  EYEBROW_CLASS,
} from "../modal/deviceModalParts";

interface Props {
  accounts: DeviceAccount[];
  selectedIds: number[];
  // Изборът на акаунт при „Неактивно" включва устройството сам (в модала).
  onSelectedChange: (ids: number[]) => void;
  // Статусът се показва само при редакция.
  showStatus: boolean;
  isEnabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  isNarrow: boolean;
}

/**
 * Секция „Касови бележки" — кои акаунти фискализират на устройството.
 * „Неактивно" не чисти избора: показва кои акаунти ще паднат при запазване и
 * пази „Отмени"; списъкът остава достъпен и избор в него включва устройството.
 */
const FiscalAccountsSection = ({
  accounts,
  selectedIds,
  onSelectedChange,
  showStatus,
  isEnabled,
  onEnabledChange,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();
  const selectedSet = new Set(selectedIds);
  const chosen = accounts.filter(
    (account) => account.takenBy === null && selectedSet.has(account.id),
  );
  // Кои ще паднат при запазване — всички избрани, както в досегашния модал.
  const removedAccounts = accounts.filter((account) => selectedSet.has(account.id));
  const removedWorkpoints = removedAccounts.filter((account) => account.kind === "workpoints").length;
  const removedUsers = removedAccounts.length - removedWorkpoints;
  const removedSummary = [
    removedWorkpoints > 0 && t("devices.pickerWorkpointsCount", { count: removedWorkpoints }),
    removedUsers > 0 && t("devices.pickerUsersCount", { count: removedUsers }),
  ]
    .filter(Boolean)
    .join(t("devices.deactivateWarningAnd"));
  const takenAccounts = accounts.filter((account) => account.takenBy !== null);
  const takenDevices = [...new Set(takenAccounts.map((account) => account.takenBy))];

  return (
    <DeviceSectionCard>
      <DeviceSectionHeader
        icon={<UserPlusIcon size={18} />}
        iconClassName="bg-brand-tint text-brand-deep-ink"
        title={t("devices.fiscalModal.accountsTitle")}
        description={t("devices.fiscalModal.accountsDesc")}
        shortDescription={t("devices.fiscalModal.accountsDescShort")}
        isNarrow={isNarrow}
        action={
          showStatus && (
            <DeviceSwitch
              size="lg"
              checked={isEnabled}
              onChange={onEnabledChange}
              label={isEnabled ? t("devices.posModal.active") : t("devices.posModal.inactive")}
              className="ml-auto"
            />
          )
        }
      />

      {!isEnabled && (
        <div
          className={`flex items-start gap-2.5 rounded-[10px] bg-warn-tint px-3 py-2.5 text-[12.5px] leading-[1.45] text-warn-ink ${
            isNarrow ? "mx-4 mt-3.5" : "mx-4 mt-3"
          }`}
        >
          <WarningIcon size={16} className="mt-px shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div>
              <b>{t("devices.fiscalModal.inactiveBannerTitle")}</b>{" "}
              {t("devices.fiscalModal.inactiveBannerText")}
            </div>
            {removedAccounts.length > 0 && (
              <div>
                <span className="font-bold">
                  {t("devices.deactivateWarning", {
                    count: removedAccounts.length,
                    summary: removedSummary,
                  })}
                </span>{" "}
                {removedAccounts.map((account) => account.name).join(", ")}
              </div>
            )}
          </div>
          {removedAccounts.length > 0 && (
            <button
              type="button"
              onClick={() => onEnabledChange(true)}
              className="shrink-0 cursor-pointer text-[12.5px] font-bold underline underline-offset-2 hover:opacity-80"
            >
              {t("devices.deactivateUndo")}
            </button>
          )}
        </div>
      )}

      {/* Изключено: приглушено, но достъпно — избор на акаунт включва устройството. */}
      <div
        className={`flex flex-col ${isNarrow ? "gap-3.5 p-4" : "gap-3 px-4 py-3"} ${
          isEnabled ? "" : "opacity-60"
        }`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className={EYEBROW_CLASS}>{t("devices.posModal.accountsLabel")}</span>
          <span className="rounded-full bg-brand-tint px-[9px] py-[3px] text-[11px] font-bold text-brand-deep-ink">
            {t("devices.posModal.selectedPill", { count: chosen.length })}
          </span>
        </div>

        <DeviceAccountList
          accounts={accounts}
          selectedIds={selectedSet}
          onChange={onSelectedChange}
          isNarrow={isNarrow}
          isDense
        />

        {takenAccounts.length > 0 && (
          <div className="flex items-start gap-2 rounded-[10px] bg-canvas px-3 py-2 text-[11.5px] leading-[1.4] text-body">
            <LockIcon size={14} className="mt-px shrink-0" />
            <span>
              {t("devices.fiscalModal.takenNote", {
                count: takenAccounts.length,
                where:
                  takenDevices.length === 1
                    ? `„${takenDevices[0]}“`
                    : t("devices.fiscalModal.takenWhereMany"),
              })}
            </span>
          </div>
        )}
      </div>
    </DeviceSectionCard>
  );
};

// memo: при писане на името в хедъра секцията не се прерисува.
export default memo(FiscalAccountsSection);
